import {
  AlertOctagon, AlertCircle, Zap, Vibrate, LogIn, LogOut, Gauge, Activity,
  BatteryLow, BatteryWarning, Unplug, SatelliteDish, Satellite, DoorOpen,
  KeyRound, Coffee, Pause, Play, Truck, Fuel, TrendingDown, TrendingUp,
  RotateCw, Power, Clock, RefreshCcw, User, MapPin, CalendarClock, Wrench,
  Bell, Thermometer, Plane, Hexagon,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// alarm-icon — mapping alarm code → lucide icon + semantic color class.
//
// PENOMORAN WAJIB mengikuti ALARM_NAMES (alarm-names.ts) yang sumbernya
// AlarmCode.java — jangan pakai skema penomoran lain (versi lama file ini
// memakai skema berbeda sehingga mis. "Keluar Geofence" (8) tampil ber-icon
// baterai).
//
// Warna ikut severity:
//   - red          : critical / kerusakan / safety  (SOS, Crash, Power Cut)
//   - amber/orange : warning / attention            (Overspeed, Low Batt)
//   - blue         : informational / location-based (Geofence, POI)
//   - emerald      : positive / recovery / start    (ACC On, Power On)
//   - slate        : neutral / stop / off           (ACC Off, IO nonaktif)
//
// Pakai map lookup by alarmId; fallback ke Bell muted untuk unknown code.
// =============================================================================

export interface AlarmVisual {
  Icon: LucideIcon;
  color: string; // Tailwind text-color class (e.g. "text-red-500")
}

const FALLBACK: AlarmVisual = { Icon: Bell, color: 'text-muted-foreground' };

const MAP: Record<number, AlarmVisual> = {
  1:  { Icon: AlertOctagon,   color: 'text-red-600'     }, // SOS / Panic
  2:  { Icon: Unplug,         color: 'text-red-500'     }, // Power Cut (colokan, ref web lama)
  3:  { Icon: BatteryWarning, color: 'text-orange-500'  }, // Battery GPS Lemah
  4:  { Icon: Vibrate,        color: 'text-amber-500'   }, // Guncangan (Shock)
  5:  { Icon: Gauge,          color: 'text-red-500'     }, // Over Speed
  6:  { Icon: Gauge,          color: 'text-amber-500'   }, // Kecepatan Rendah
  7:  { Icon: LogIn,          color: 'text-blue-500'    }, // Masuk Geofence
  8:  { Icon: LogOut,         color: 'text-orange-500'  }, // Keluar Geofence
  9:  { Icon: Clock,          color: 'text-amber-500'   }, // Parkir Melebihi Batas
  10: { Icon: Truck,          color: 'text-red-500'     }, // Kendaraan Berpindah
  11: { Icon: Pause,          color: 'text-amber-500'   }, // Idle
  12: { Icon: Wrench,         color: 'text-amber-600'   }, // Pengingat Servis
  13: { Icon: MapPin,         color: 'text-orange-500'  }, // Keluar Rute

  // IO1-IO6 aktif/nonaktif
  14: { Icon: Zap, color: 'text-emerald-500' }, // IO1 Aktif
  15: { Icon: Zap, color: 'text-slate-400'   }, // IO1 Nonaktif
  16: { Icon: Zap, color: 'text-emerald-500' }, // IO2 Aktif
  17: { Icon: Zap, color: 'text-slate-400'   }, // IO2 Nonaktif
  18: { Icon: Zap, color: 'text-emerald-500' }, // IO3 Aktif
  19: { Icon: Zap, color: 'text-slate-400'   }, // IO3 Nonaktif
  20: { Icon: Zap, color: 'text-emerald-500' }, // IO4 Aktif
  21: { Icon: Zap, color: 'text-slate-400'   }, // IO4 Nonaktif
  22: { Icon: Zap, color: 'text-slate-400'   }, // IO5 Nonaktif
  23: { Icon: Zap, color: 'text-emerald-500' }, // IO5 Aktif
  24: { Icon: Zap, color: 'text-slate-400'   }, // IO6 Nonaktif
  25: { Icon: Zap, color: 'text-emerald-500' }, // IO6 Aktif

  26: { Icon: AlertCircle,   color: 'text-red-500'     }, // Antena GSM Putus
  27: { Icon: SatelliteDish, color: 'text-red-500'     }, // Antena GPS Putus
  28: { Icon: AlertOctagon,  color: 'text-red-600'     }, // Jammer Aktif
  29: { Icon: AlertCircle,   color: 'text-red-500'     }, // GSM Jammed
  30: { Icon: AlertCircle,   color: 'text-emerald-500' }, // GSM Jammed Selesai
  31: { Icon: Satellite,     color: 'text-emerald-500' }, // GPS Jammed Selesai
  32: { Icon: Coffee,        color: 'text-amber-600'   }, // Driver Mengantuk
  33: { Icon: Coffee,        color: 'text-emerald-500' }, // Fatigue Selesai
  34: { Icon: Pause,         color: 'text-slate-400'   }, // Masuk Sleep Mode
  35: { Icon: Play,          color: 'text-emerald-500' }, // Keluar Sleep Mode
  36: { Icon: TrendingUp,    color: 'text-amber-500'   }, // Akselerasi
  37: { Icon: TrendingDown,  color: 'text-amber-500'   }, // Berhenti Mendadak
  38: { Icon: Power,         color: 'text-emerald-500' }, // Daya Eksternal Terhubung
  39: { Icon: BatteryLow,    color: 'text-orange-500'  }, // Daya Eksternal Lemah
  40: { Icon: DoorOpen,      color: 'text-amber-500'   }, // Pintu Dibuka
  41: { Icon: DoorOpen,      color: 'text-slate-400'   }, // Pintu Ditutup
  42: { Icon: Clock,         color: 'text-red-500'     }, // Pengiriman Melebihi Waktu
  43: { Icon: MapPin,        color: 'text-emerald-500' }, // Tiba di Tujuan
  44: { Icon: Clock,         color: 'text-amber-500'   }, // Parkir Lama di Geofence
  45: { Icon: Activity,      color: 'text-amber-500'   }, // Bergerak (Range Tanggal)
  46: { Icon: MapPin,        color: 'text-blue-500'    }, // Masuk POI
  47: { Icon: MapPin,        color: 'text-orange-500'  }, // Keluar POI
  48: { Icon: KeyRound,      color: 'text-emerald-500' }, // ACC On
  49: { Icon: KeyRound,      color: 'text-slate-400'   }, // ACC Off
  50: { Icon: AlertCircle,   color: 'text-amber-500'   }, // Tidak Update
  51: { Icon: Thermometer,   color: 'text-red-500'     }, // Alarm Suhu
  52: { Icon: Fuel,          color: 'text-red-500'     }, // Alarm Oli/BBM
  53: { Icon: AlertOctagon,  color: 'text-red-600'     }, // Kendaraan Kecelakaan
  54: { Icon: SatelliteDish, color: 'text-red-500'     }, // GPS Dilepas
  55: { Icon: Bell,          color: 'text-amber-500'   }, // Alarm Umum
  56: { Icon: Clock,         color: 'text-amber-500'   }, // Kerja Lembur
  57: { Icon: Power,         color: 'text-emerald-500' }, // Power On
  58: { Icon: SatelliteDish, color: 'text-red-500'     }, // GPS Dibongkar
  59: { Icon: Satellite,     color: 'text-slate-400'   }, // Masuk Dead Zone
  60: { Icon: Satellite,     color: 'text-blue-500'    }, // Keluar Dead Zone
  61: { Icon: Satellite,     color: 'text-amber-500'   }, // Tidak Ada Data Satelit
  62: { Icon: Satellite,     color: 'text-amber-500'   }, // Tidak Ada Satelit
  63: { Icon: RotateCw,      color: 'text-amber-500'   }, // Menikung Tajam
  64: { Icon: DoorOpen,      color: 'text-red-500'     }, // Casing GPS Dibuka
  65: { Icon: Plane,         color: 'text-slate-400'   }, // Mode Pesawat
  66: { Icon: RefreshCcw,    color: 'text-amber-500'   }, // SIM Diganti
  67: { Icon: User,          color: 'text-blue-500'    }, // DMS (Driver Monitoring)
  68: { Icon: Activity,      color: 'text-blue-500'    }, // ADAS
  69: { Icon: AlertOctagon,  color: 'text-red-600'     }, // Peringatan Tabrakan Depan
  70: { Icon: MapPin,        color: 'text-amber-500'   }, // Keluar Jalur
  71: { Icon: AlertOctagon,  color: 'text-red-600'     }, // Tabrakan Terdeteksi
  72: { Icon: TrendingDown,  color: 'text-amber-500'   }, // Rem Mendadak
  73: { Icon: User,          color: 'text-red-500'     }, // Sabuk Pengaman Lepas
  74: { Icon: Fuel,          color: 'text-red-500'     }, // BBM Dicuri/Rendah
  75: { Icon: AlertCircle,   color: 'text-red-500'     }, // SD Card Error
  76: { Icon: Vibrate,       color: 'text-amber-500'   }, // Getaran Terdeteksi
  77: { Icon: Coffee,        color: 'text-amber-600'   }, // Kelelahan Driver

  // Custom internal
  9001: { Icon: CalendarClock, color: 'text-amber-600' }, // Rental Jatuh Tempo
  9002: { Icon: Wrench,        color: 'text-amber-600' }, // Maintenance Due
  9003: { Icon: Hexagon,       color: 'text-amber-500' }, // Geofence Worker Alert
};

export function alarmVisual(code: number): AlarmVisual {
  return MAP[code] ?? FALLBACK;
}
